// Reading activity for the Reading users page: the summary panel and the drawer's Activity section.
import React, { useMemo, useState } from 'react';
import { FiActivity, FiCheckCircle, FiClock, FiUsers } from 'react-icons/fi';
import { Badge, Kpi, Notice, Skeleton } from '../UI';
import BarChart from '../UI/BarChart';
import { useCachedQuery } from '../Hooks/useCachedQuery';
import { fetchActivitySummary, fetchReaderActivity } from '../../Utils/ReaderUsersAPI';
import { ReaderActivity, ReadingSession } from '../../Models/AdminUsers';
import { formatDate, formatDateTime, formatMinutes, formatNumber, timeAgo } from '../../Utils/Format';

export const TRACKING_STARTED = 'October 31, 2026';
const RANGES = [7, 30, 90];
const plural = (count: number, word: string) => `${formatNumber(count)} ${count === 1 ? word : `${word}s`}`;

const NoSessions: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`activity-empty${compact ? ' activity-empty--compact' : ''}`}>
    <FiClock />
    <p>No reading sessions recorded yet. Time tracking started on {TRACKING_STARTED}.</p>
  </div>
);

const levelText = (level: string | number | null | undefined) => {
  if (level === null || level === undefined || level === '') return '';
  return typeof level === 'number' || /^\d+$/.test(level) ? `Level ${level}` : String(level);
};

const LevelTag: React.FC<{ level: string | number | null | undefined }> = ({ level }) => {
  const text = levelText(level);
  return text ? <span className="level-tag">{text}</span> : null;
};

// ---------- Summary panel ----------

export const ActivitySummaryPanel: React.FC<{
  days: number;
  onDays: (days: number) => void;
  onOpenReader: (id: string) => void;
  /** Filter the user list to readers behind a card (null clears it). */
  onReveal?: (filter: { read: number; finished: boolean } | null) => void;
  revealed?: { read: number; finished: boolean } | null;
}> = ({ days, onDays, onOpenReader, onReveal, revealed }) => {
  const isRevealed = (finished: boolean) => !!revealed && revealed.read === days && revealed.finished === finished;
  const reveal = (finished: boolean) => onReveal && onReveal(isRevealed(finished) ? null : { read: days, finished });
  const summary = useCachedQuery(`rd:activity:${days}`, (signal) => fetchActivitySummary(days, signal), 60000);
  const s = summary.data;
  const firstLoad = summary.loading && !s && !summary.error;
  const unavailable = !summary.loading && !summary.error && s === null;
  const empty = unavailable || (!!s && s.sessions === 0);
  const chartData = useMemo(
    () => (s && Array.isArray(s.daily) ? s.daily.map((d) => ({ date: d.date, count: d.minutes || 0 })) : []),
    [s],
  );

  return (
    <section className="panel activity-panel">
      <div className="panel__head">
        <div>
          <h2>Reading activity</h2>
          <p>Time spent reading and books finished, last {days} days</p>
        </div>
        <div className="chips" role="group" aria-label="Activity range">
          {RANGES.map((value) => (
            <button key={value} className={days === value ? 'is-active' : ''} onClick={() => onDays(value)}>
              {value} days
            </button>
          ))}
        </div>
      </div>

      {summary.error && (
        <Notice tone="error" style={{ marginBottom: '1.6rem' }}>
          Could not load reading activity: {summary.error.message}
        </Notice>
      )}

      {!unavailable && (
        <div className={`grid grid--kpi activity-kpis${summary.loading && s ? ' is-refreshing' : ''}`}>
          <Kpi
            label="Active readers"
            onClick={onReveal ? () => reveal(false) : undefined}
            active={isRevealed(false)}
            title={"Show readers who read in this range"}
            icon={<FiUsers />}
            value={formatNumber(s ? s.activeReaders : null)}
            loading={firstLoad}
            sub={s ? `${formatNumber(s.signedInReaders)} signed in, ${formatNumber(s.anonymousReaders)} guests` : ' '}
          />
          <Kpi
            label="Time read"
            onClick={onReveal ? () => reveal(false) : undefined}
            active={isRevealed(false)}
            title={"Show readers who read in this range"}
            icon={<FiClock />}
            tone="coral"
            value={formatMinutes(s ? s.minutes : null)}
            loading={firstLoad}
            sub={s ? `${formatNumber(s.sessions)} reading sessions` : ' '}
          />
          <Kpi
            label="Books finished"
            onClick={onReveal ? () => reveal(true) : undefined}
            active={isRevealed(true)}
            title={"Show readers who finished a book in this range"}
            icon={<FiCheckCircle />}
            tone="green"
            value={formatNumber(s ? s.booksFinished : null)}
            loading={firstLoad}
            sub={s && s.sessions ? `${Math.round((s.booksFinished / s.sessions) * 100)}% of sessions` : ' '}
          />
          <Kpi
            label="Avg per reader"
            onClick={onReveal ? () => reveal(false) : undefined}
            active={isRevealed(false)}
            title={"Show readers who read in this range"}
            icon={<FiActivity />}
            tone="amber"
            value={formatMinutes(s ? s.avgMinutesPerReader : null)}
            loading={firstLoad}
            sub={`Over ${days} days`}
          />
        </div>
      )}

      {firstLoad && <Skeleton width="100%" height="18rem" block />}
      {empty && <NoSessions />}

      {s && !empty && (
        <>
          <div className="activity-chart">
            <div className="activity-subhead">Minutes read per day</div>
            <BarChart
              data={chartData}
              unit="read"
              format={formatMinutes}
              detail={(index) => {
                const day = s.daily[index];
                return day ? `${formatNumber(day.readers)} ${day.readers === 1 ? 'reader' : 'readers'}` : null;
              }}
            />
          </div>

          <div className="activity-tables">
            <div>
              <div className="activity-subhead">Top stories</div>
              <div className="mini-table-scroll">
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Story</th>
                      <th className="num">Readers</th>
                      <th className="num">Time</th>
                      <th className="num hide-sm">Finished</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.topStories || []).map((story) => (
                      <tr key={story.storyId}>
                        <td className="mini-table__grow">
                          <div className="mini-table__title">{story.title || 'Untitled story'}</div>
                          <div className="mini-table__meta">
                            <LevelTag level={story.level} />
                            <span>{plural(story.sessions, 'session')}</span>
                          </div>
                        </td>
                        <td className="num">{formatNumber(story.readers)}</td>
                        <td className="num">{formatMinutes(story.minutes)}</td>
                        <td className="num hide-sm">{formatNumber(story.finished)}</td>
                      </tr>
                    ))}
                    {(!s.topStories || s.topStories.length === 0) && (
                      <tr>
                        <td colSpan={4} className="muted">
                          No stories read in this range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="activity-subhead">Top readers</div>
              <div className="mini-table-scroll">
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Reader</th>
                      <th className="num">Time</th>
                      <th className="num hide-sm">Books</th>
                      <th className="num">Last read</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.topReaders || []).map((reader) => (
                      <tr
                        key={reader.id}
                        className="is-clickable"
                        tabIndex={0}
                        onClick={() => onOpenReader(reader.id)}
                        onKeyDown={(event) => event.key === 'Enter' && onOpenReader(reader.id)}
                        title="Open reader details"
                      >
                        <td className="mini-table__grow">
                          <div className="mini-table__title">{reader.name || 'Unnamed reader'}</div>
                          <div className="mini-table__meta">
                            <span style={{ textTransform: 'capitalize' }}>{reader.accountType || 'reader'}</span>
                            <span>{plural(reader.sessions, 'session')}</span>
                          </div>
                        </td>
                        <td className="num">{formatMinutes(reader.minutes)}</td>
                        <td className="num hide-sm">{formatNumber(reader.finished)}</td>
                        <td className="num" title={formatDateTime(reader.lastAt)}>
                          {reader.lastAt ? timeAgo(reader.lastAt) : '–'}
                        </td>
                      </tr>
                    ))}
                    {(!s.topReaders || s.topReaders.length === 0) && (
                      <tr>
                        <td colSpan={4} className="muted">
                          No signed in readers in this range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

// ---------- Drawer section ----------

const dayKey = (iso: string) => {
  const date = new Date(iso);
  return isNaN(date.valueOf()) ? 'unknown' : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const dayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const timeFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

const dayLabel = (iso: string) => {
  const date = new Date(iso);
  if (isNaN(date.valueOf())) return 'Unknown date';
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return 'Today';
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return 'Yesterday';
  const label = dayFormat.format(date);
  return date.getFullYear() === today.getFullYear() ? label : `${label}, ${date.getFullYear()}`;
};

const groupByDay = (sessions: ReadingSession[]) => {
  const groups: { key: string; label: string; minutes: number; sessions: ReadingSession[] }[] = [];
  const sorted = sessions.slice().sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  sorted.forEach((session) => {
    const key = dayKey(session.startedAt);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, label: dayLabel(session.startedAt), minutes: 0, sessions: [] };
      groups.push(group);
    }
    group.minutes += session.minutes || 0;
    group.sessions.push(session);
  });
  return groups;
};

const DAYS_PER_PAGE = 7;

export const ReaderActivitySection: React.FC<{ id: string }> = ({ id }) => {
  const activity = useCachedQuery<ReaderActivity | null>(
    `rd:user-activity:${id}`,
    (signal) => fetchReaderActivity(id, signal),
    60000,
  );
  const [visibleDays, setVisibleDays] = useState(DAYS_PER_PAGE);
  const data = activity.data;
  const sessions = data && Array.isArray(data.sessions) ? data.sessions : [];
  const groups = useMemo(() => groupByDay(sessions), [sessions]);
  const showReader = !!data && (data.readers || []).length > 1;
  const t = data && data.totals;

  return (
    <div className="drawer__section">
      <h3>Reading activity</h3>
      {activity.loading && !data && !activity.error && <Skeleton width="100%" height="9rem" block />}
      {activity.error && <Notice tone="error">Could not load reading activity: {activity.error.message}</Notice>}
      {!activity.loading && !activity.error && (!data || (sessions.length === 0 && !(t && t.minutesAll))) && (
        <NoSessions compact />
      )}

      {data && t && (sessions.length > 0 || !!t.minutesAll) && (
        <>
          <div className="mini-stats">
            <div>
              <span>This week</span>
              <strong>{formatMinutes(t.minutes7d)}</strong>
            </div>
            <div>
              <span>Last 30 days</span>
              <strong>{formatMinutes(t.minutes30d)}</strong>
            </div>
            <div>
              <span>All time</span>
              <strong>{formatMinutes(t.minutesAll)}</strong>
            </div>
            <div>
              <span>Books finished</span>
              <strong>{formatNumber(t.booksFinished)}</strong>
            </div>
            <div>
              <span>Days active</span>
              <strong>
                {formatNumber(t.daysActive30d)}
                <small> of 30</small>
              </strong>
            </div>
            <div>
              <span>Sessions</span>
              <strong>{formatNumber(t.sessions)}</strong>
            </div>
          </div>
          {t.lastActive && (
            <p className="muted activity-last">
              Last read {timeAgo(t.lastActive)} ({formatDateTime(t.lastActive)})
            </p>
          )}

          <div className="session-days">
            {groups.slice(0, visibleDays).map((group) => (
              <div key={group.key} className="session-day">
                <div className="session-day__head">
                  <strong>{group.label}</strong>
                  <span>
                    {formatMinutes(group.minutes)} · {group.sessions.length}{' '}
                    {group.sessions.length === 1 ? 'session' : 'sessions'}
                  </span>
                </div>
                <ul className="session-list">
                  {group.sessions.map((session) => (
                    <li key={session.id}>
                      <span className="session-list__time">
                        {isNaN(new Date(session.startedAt).valueOf())
                          ? '–'
                          : timeFormat.format(new Date(session.startedAt))}
                      </span>
                      <div className="session-list__main">
                        <div className="session-list__title">
                          {session.storyTitle || 'Untitled story'}
                          {session.completed && <Badge tone="paid">Finished</Badge>}
                        </div>
                        <div className="session-list__meta">
                          {showReader && session.readerName && (
                            <span className="session-list__reader">{session.readerName}</span>
                          )}
                          <LevelTag level={session.storyLevel} />
                          {session.pageCount > 0 && (
                            <span>
                              {formatNumber(session.pagesSeen)} / {formatNumber(session.pageCount)} pages
                            </span>
                          )}
                          {session.device && <span>{session.device}</span>}
                        </div>
                      </div>
                      <span className="session-list__minutes">{formatMinutes(session.minutes)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {groups.length > visibleDays && (
            <button className="btn--ghost btn--sm" onClick={() => setVisibleDays(visibleDays + DAYS_PER_PAGE)}>
              Show earlier days ({groups.length - visibleDays} more)
            </button>
          )}
          {sessions.length === 0 && <p className="muted">No sessions in the recent history.</p>}
        </>
      )}
    </div>
  );
};

// ---------- Stories and words (existing history, tidier) ----------

const scoreText = (score: number | string) => {
  if (typeof score === 'number')
    return score <= 1 && score > 0 && !Number.isInteger(score) ? `${Math.round(score * 100)}%` : String(score);
  return score;
};

const STORIES_PER_PAGE = 8;

export const StoriesOpenedSection: React.FC<{
  stories: { title?: string; date: string | null; scores?: (number | string)[] }[];
}> = ({ stories }) => {
  const [visible, setVisible] = useState(STORIES_PER_PAGE);
  return (
    <div className="drawer__section">
      <h3>
        Stories opened
        {stories.length > 0 ? <span className="drawer__count">{formatNumber(stories.length)}</span> : null}
      </h3>
      {stories.length === 0 && <p className="muted">No stories opened yet.</p>}
      {stories.length > 0 && (
        <ul className="timeline">
          {stories.slice(0, visible).map((story, index) => {
            const scores = (story.scores || []).filter(
              (score) => score !== null && score !== undefined && score !== '',
            );
            return (
              <li key={index}>
                <span className="timeline__dot timeline__dot--coral" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="timeline__title">{story.title || 'Untitled story'}</div>
                  <div className="timeline__meta">
                    {formatDate(story.date) === '–' ? 'Date unknown' : formatDateTime(story.date)}
                    {scores.length > 0 && (
                      <>
                        {' · '}
                        Quiz {scores.length === 1 ? 'score' : 'scores'} {scores.map(scoreText).join(', ')}
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {stories.length > visible && (
        <button className="btn--ghost btn--sm" onClick={() => setVisible(stories.length)}>
          Show all {formatNumber(stories.length)} stories
        </button>
      )}
    </div>
  );
};

export const WordsTappedSection: React.FC<{
  words?: { word: string; timesClicked?: number; times_clicked?: number }[];
}> = ({ words }) => {
  if (!words || words.length === 0) return null;
  const counted = words
    .map((item) => ({
      word: item.word,
      count: item.timesClicked !== undefined ? item.timesClicked : item.times_clicked || 0,
    }))
    .filter((item) => item.word)
    .sort((a, b) => b.count - a.count);
  return (
    <div className="drawer__section">
      <h3>
        Words tapped for help<span className="drawer__count">{formatNumber(counted.length)}</span>
      </h3>
      <div className="word-chips">
        {counted.slice(0, 40).map((item) => (
          <span
            key={item.word}
            className="word-chip"
            title={`Tapped ${item.count} ${item.count === 1 ? 'time' : 'times'}`}
          >
            {item.word}
            {item.count > 0 && <b>{item.count}</b>}
          </span>
        ))}
      </div>
      {counted.length > 40 && (
        <p className="muted" style={{ marginTop: '0.8rem' }}>
          Showing the 40 most tapped words.
        </p>
      )}
    </div>
  );
};
